import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type RefreshHandler = () => void | Promise<void>;

interface PortalUIContextValue {
  stickyTitle: string | null;
  setStickyTitle: (title: string | null) => void;
  onRefresh: RefreshHandler | null;
  setOnRefresh: (fn: RefreshHandler | null) => void;
  isRefreshing: boolean;
  setIsRefreshing: (refreshing: boolean) => void;
}

const PortalUIContext = createContext<PortalUIContextValue | null>(null);

export const PortalUIProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [stickyTitle, setStickyTitle] = useState<string | null>(null);
  const [onRefresh, setOnRefreshState] = useState<RefreshHandler | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // `useState` treats functions as updaters; wrap so callers can pass the
  // handler directly without the `() => fn` boilerplate.
  const setOnRefresh = useCallback((fn: RefreshHandler | null) => {
    setOnRefreshState(() => fn);
  }, []);

  const value = useMemo<PortalUIContextValue>(
    () => ({
      stickyTitle,
      setStickyTitle,
      onRefresh,
      setOnRefresh,
      isRefreshing,
      setIsRefreshing,
    }),
    [stickyTitle, onRefresh, setOnRefresh, isRefreshing],
  );

  return (
    <PortalUIContext.Provider value={value}>{children}</PortalUIContext.Provider>
  );
};

export const usePortalUI = (): PortalUIContextValue => {
  const ctx = useContext(PortalUIContext);
  if (!ctx) {
    throw new Error('usePortalUI must be used inside a <PortalUIProvider>');
  }
  return ctx;
};
